import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, ChevronRight, Download, FileArchive, FolderInput, FolderPlus, FolderUp, Home, Pencil, Share2, Trash2, Upload, Users, X } from "lucide-react";
import { useMe } from "../App";
import { api, qs, startDownload, useApi } from "../lib/api";
import { bytes, fullDate, nameProblem, parentOf, segments, when } from "../lib/format";
import { enqueue, fromDrop, fromInput, setUploadFinished } from "../lib/upload";
import { ExecutableWarning, MoveDialog, ShareDialog, ZipDialog } from "../components/dialogs";
import { FileIcon } from "../components/icons";
import { Button, ConfirmDialog, cx, Empty, ErrorNote, PromptDialog, Spinner, toast } from "../components/ui";

interface Entry {
  path: string;
  name: string;
  size?: number;
  modified?: string;
  shared: number;
}
interface Listing {
  owner: string;
  ownerName: string;
  path: string;
  readOnly: boolean;
  shareRoot: string | null;
  folders: Entry[];
  files: Entry[];
}
type Sort = { by: "name" | "size" | "modified"; dir: 1 | -1 };
type Dialog =
  | { kind: "newFolder" }
  | { kind: "rename"; path: string }
  | { kind: "move"; paths: string[] }
  | { kind: "delete"; paths: string[] }
  | { kind: "share"; path: string }
  | { kind: "zip"; paths: string[] }
  | { kind: "exe"; name: string; url: string; sharedBy: { name: string; email: string } | null }
  | null;

export default function Files({ shared = false }: { shared?: boolean }) {
  const { me, refreshMe } = useMe();
  const [params, setParams] = useSearchParams();
  const path = params.get("path") ?? "";
  const owner = shared ? (params.get("owner") ?? undefined) : undefined;
  const listing = useApi<Listing>(`/files?${qs({ path, owner })}`);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<Sort>({ by: "name", dir: 1 });
  const [dialog, setDialog] = useState<Dialog>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const readOnly = shared || listing.data?.readOnly;

  useEffect(() => setSelected(new Set()), [path, owner]);
  useEffect(() => {
    if (readOnly) return;
    let t: ReturnType<typeof setTimeout> | undefined;
    // Refresh the listing (debounced) as uploads finish.
    setUploadFinished(() => {
      clearTimeout(t);
      t = setTimeout(() => {
        void listing.reload();
        void refreshMe();
      }, 600);
    });
    return () => {
      clearTimeout(t);
      setUploadFinished(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, listing.reload]);

  const go = (p: string) => setParams(owner ? { owner, path: p } : p ? { path: p } : {});
  const refresh = async () => {
    setSelected(new Set());
    await Promise.all([listing.reload(), refreshMe()]);
  };

  const rows = useMemo(() => {
    const d = listing.data;
    if (!d) return [];
    const cmp = (a: Entry, b: Entry) => {
      const v = sort.by === "name" ? a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }) : sort.by === "size" ? (a.size ?? 0) - (b.size ?? 0) : (a.modified ?? "").localeCompare(b.modified ?? "");
      return v * sort.dir;
    };
    return [...[...d.folders].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }) * (sort.by === "name" ? sort.dir : 1)), ...[...d.files].sort(cmp)];
  }, [listing.data, sort]);

  const sel = [...selected];
  const one = sel.length === 1 ? sel[0] : null;
  const selFiles = sel.filter((p) => !p.endsWith("/"));
  const toggle = (p: string) => {
    const n = new Set(selected);
    if (n.has(p)) n.delete(p);
    else n.add(p);
    setSelected(n);
  };
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.path));

  const download = async (p: string) => {
    try {
      const r = await api<{ url: string; executable: boolean; sharedBy: { name: string; email: string } | null }>(`/download?${qs({ owner, path: p })}`);
      if (r.executable) setDialog({ kind: "exe", name: p.slice(p.lastIndexOf("/") + 1), url: r.url, sharedBy: r.sharedBy });
      else startDownload(r.url);
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  const downloadSelected = async () => {
    if (selFiles.length === 1 && sel.length === 1) return download(selFiles[0]);
    setDialog({ kind: "zip", paths: sel });
  };

  const upload = (list: { file: File; path: string }[]) => {
    const tooBig = list.filter((f) => f.file.size > me.maxFileBytes);
    if (tooBig.length) toast(`${tooBig.length} file${tooBig.length === 1 ? " is" : "s are"} over the ${bytes(me.maxFileBytes)} limit and won't upload`, "error");
    const ok = list.filter((f) => f.file.size <= me.maxFileBytes);
    const adding = ok.reduce((n, f) => n + f.file.size, 0);
    if (me.usageBytes + adding > me.quotaBytes) return toast(`Not enough space: that's ${bytes(adding)} and you have ${bytes(Math.max(0, me.quotaBytes - me.usageBytes))} left`, "error");
    if (ok.length) enqueue(ok);
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (readOnly) return;
    upload(await fromDrop(e.dataTransfer, path));
  };

  const d = listing.data;
  return (
    <div
      className="relative min-h-full"
      onDragOver={(e) => {
        if (readOnly || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={onDrop}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
        <Breadcrumb listing={d} path={path} shared={shared} go={go} />

        <div className="flex flex-wrap items-center gap-2 mt-4 min-h-10">
          {sel.length > 0 ? (
            <>
              <span className="text-sm font-medium mr-1 tabular">{sel.length} selected</span>
              <Button onClick={downloadSelected}>
                {sel.length === 1 && selFiles.length === 1 ? <Download className="size-4" /> : <FileArchive className="size-4" />}
                {sel.length === 1 && selFiles.length === 1 ? "Download" : "Download ZIP"}
              </Button>
              {!readOnly && one && (
                <Button onClick={() => setDialog({ kind: "share", path: one })}>
                  <Share2 className="size-4" /> Share
                </Button>
              )}
              {!readOnly && one && (
                <Button onClick={() => setDialog({ kind: "rename", path: one })}>
                  <Pencil className="size-4" /> Rename
                </Button>
              )}
              {!readOnly && (
                <Button onClick={() => setDialog({ kind: "move", paths: sel })}>
                  <FolderInput className="size-4" /> Move
                </Button>
              )}
              {!readOnly && (
                <Button onClick={() => setDialog({ kind: "delete", paths: sel })}>
                  <Trash2 className="size-4" /> Delete
                </Button>
              )}
              <Button variant="ghost" onClick={() => setSelected(new Set())} aria-label="Clear selection">
                <X className="size-4" />
              </Button>
            </>
          ) : readOnly ? (
            <p className="text-sm text-muted">Shared with you by {d?.ownerName ?? "…"} · view and download only</p>
          ) : (
            <>
              <Button variant="primary" onClick={() => fileInput.current?.click()}>
                <Upload className="size-4" /> Upload files
              </Button>
              <Button onClick={() => folderInput.current?.click()}>
                <FolderUp className="size-4" /> Upload folder
              </Button>
              <Button onClick={() => setDialog({ kind: "newFolder" })}>
                <FolderPlus className="size-4" /> New folder
              </Button>
              <span className="hidden sm:inline text-sm text-muted ml-2">or drop files and folders here</span>
            </>
          )}
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => (e.target.files && upload(fromInput(e.target.files, path)), (e.target.value = ""))} />
          <input
            ref={folderInput}
            type="file"
            hidden
            {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            onChange={(e) => (e.target.files && upload(fromInput(e.target.files, path)), (e.target.value = ""))}
          />
        </div>

        <div className="mt-4">
          {listing.error ? (
            <ErrorNote>{listing.error}</ErrorNote>
          ) : !d ? (
            <Spinner />
          ) : !rows.length ? (
            <div className="border border-dashed border-line rounded-xl">
              <Empty icon={<Upload className="size-8" />} title={readOnly ? "This folder is empty" : path ? "This folder is empty" : "No files yet"}>
                {!readOnly && "Upload files or folders, or drag them here."}
              </Empty>
            </div>
          ) : (
            <div className="border border-line rounded-xl bg-surface overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 text-muted text-xs">
                  <tr>
                    <th className="w-10 pl-4">
                      <input type="checkbox" aria-label="Select all" checked={allChecked} onChange={() => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.path)))} />
                    </th>
                    <SortHead label="Name" by="name" sort={sort} setSort={setSort} className="text-left" />
                    <SortHead label="Size" by="size" sort={sort} setSort={setSort} className="text-right w-24 hidden sm:table-cell" />
                    <SortHead label="Modified" by="modified" sort={sort} setSort={setSort} className="text-right w-40 pr-4 hidden md:table-cell" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((r) => {
                    const folder = r.path.endsWith("/");
                    return (
                      <tr key={r.path} className={cx("hover:bg-surface-2/60", selected.has(r.path) && "bg-accent-soft")}>
                        <td className="pl-4">
                          <input type="checkbox" aria-label={`Select ${r.name}`} checked={selected.has(r.path)} onChange={() => toggle(r.path)} />
                        </td>
                        <td className="py-2.5 pr-3 max-w-0">
                          <button className="flex items-center gap-3 min-w-0 max-w-full text-left hover:underline" onClick={() => (folder ? go(r.path) : download(r.path))} title={folder ? `Open ${r.name}` : `Download ${r.name}`}>
                            <FileIcon name={r.name} folder={folder} />
                            <span className="truncate">{r.name}</span>
                          </button>
                          {!folder && (
                            <span className="sm:hidden block text-xs text-muted ml-8 tabular">
                              {bytes(r.size)} · {when(r.modified)}
                            </span>
                          )}
                          {r.shared > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-accent-text ml-8 mt-0.5" title={`Shared with ${r.shared} ${r.shared === 1 ? "person" : "people"}`}>
                              <Users className="size-3" /> Shared with {r.shared}
                            </span>
                          )}
                        </td>
                        <td className="text-right text-muted tabular hidden sm:table-cell">{folder ? "" : bytes(r.size)}</td>
                        <td className="text-right text-muted pr-4 hidden md:table-cell" title={fullDate(r.modified)}>
                          {folder ? "" : when(r.modified)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {dragging && (
        <div className="pointer-events-none fixed inset-0 md:left-64 z-30 m-3 rounded-2xl border-2 border-dashed border-accent bg-accent-soft/80 flex items-center justify-center">
          <div className="text-accent-text font-medium flex items-center gap-2">
            <Upload className="size-5" /> Drop to upload to {path ? segments(path).at(-1) : "My files"}
          </div>
        </div>
      )}

      {dialog?.kind === "newFolder" && (
        <PromptDialog
          title="New folder"
          label="Folder name"
          confirm="Create"
          validate={nameProblem}
          onClose={() => setDialog(null)}
          onSubmit={async (name) => {
            await api("/folders", { body: { path: `${path}${name}/` } });
            await refresh();
          }}
        />
      )}
      {dialog?.kind === "rename" && (
        <PromptDialog
          title="Rename"
          label="New name"
          initial={segments(dialog.path).at(-1)}
          confirm="Rename"
          validate={nameProblem}
          onClose={() => setDialog(null)}
          onSubmit={async (name) => {
            const folder = dialog.path.endsWith("/");
            const r = await api<{ jobId?: string }>("/move", { body: { from: dialog.path, to: parentOf(dialog.path) + name + (folder ? "/" : "") } });
            if (r.jobId) toast("Renaming a large folder; it'll update in a moment.");
            await refresh();
          }}
        />
      )}
      {dialog?.kind === "move" && <MoveDialog paths={dialog.paths} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.kind === "delete" && (
        <ConfirmDialog
          title={dialog.paths.length === 1 ? `Delete "${segments(dialog.paths[0]).at(-1)}"?` : `Delete ${dialog.paths.length} items?`}
          confirm="Delete"
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await api("/delete", { body: { paths: dialog.paths } });
            toast("Moved to Recently deleted. You can restore it for 30 days.");
            await refresh();
          }}
        >
          {dialog.paths.length > 1 && (
            <ul className="list-disc pl-5 text-ink">
              {dialog.paths.slice(0, 6).map((p) => (
                <li key={p} className="truncate">
                  {segments(p).at(-1)}
                  {p.endsWith("/") && " (folder and everything in it)"}
                </li>
              ))}
              {dialog.paths.length > 6 && <li>and {dialog.paths.length - 6} more</li>}
            </ul>
          )}
          <p>Deleted files stay in Recently deleted for 30 days. Anyone you shared them with loses access now.</p>
        </ConfirmDialog>
      )}
      {dialog?.kind === "share" && <ShareDialog path={dialog.path} onClose={() => setDialog(null)} onChanged={() => void listing.reload()} />}
      {dialog?.kind === "zip" && <ZipDialog owner={owner} paths={dialog.paths} onClose={() => setDialog(null)} />}
      {dialog?.kind === "exe" && <ExecutableWarning name={dialog.name} sharedBy={dialog.sharedBy} onConfirm={() => startDownload(dialog.url)} onClose={() => setDialog(null)} />}
    </div>
  );
}

function SortHead({ label, by, sort, setSort, className }: { label: string; by: Sort["by"]; sort: Sort; setSort: (s: Sort) => void; className?: string }) {
  const active = sort.by === by;
  return (
    <th className={cx("py-2.5 font-medium", className)} aria-sort={active ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
      <button className="inline-flex items-center gap-1 hover:text-ink" onClick={() => setSort({ by, dir: active ? ((-sort.dir) as 1 | -1) : 1 })}>
        {label}
        {active && (sort.dir === 1 ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    </th>
  );
}

function Breadcrumb({ listing, path, shared, go }: { listing: Listing | null; path: string; shared: boolean; go: (p: string) => void }) {
  const segs = segments(path);
  // In a shared folder, the trail starts at the shared folder itself; nothing above it is visible.
  const start = shared && listing?.shareRoot ? Math.max(0, segments(listing.shareRoot).length - 1) : 0;
  return (
    <nav className="flex items-center flex-wrap gap-1 text-lg" aria-label="Folder">
      {shared ? (
        <>
          <Link to="/shared" className="font-semibold px-1 rounded hover:bg-surface-2">
            Shared with me
          </Link>
          {listing && (
            <>
              <ChevronRight className="size-4 text-muted" />
              <span className="text-muted px-1">{listing.ownerName}</span>
            </>
          )}
        </>
      ) : (
        <button onClick={() => go("")} className="inline-flex items-center gap-2 font-semibold px-1 rounded hover:bg-surface-2">
          <Home className="size-5" /> My files
        </button>
      )}
      {segs.slice(start).map((s, i) => {
        const to = segs.slice(0, start + i + 1).join("/") + "/";
        const last = start + i === segs.length - 1;
        return (
          <span key={to} className="inline-flex items-center gap-1 min-w-0">
            <ChevronRight className="size-4 text-muted shrink-0" />
            {last ? (
              <span className="font-semibold px-1 truncate">{s}</span>
            ) : (
              <button onClick={() => go(to)} className="px-1 rounded hover:bg-surface-2 truncate">
                {s}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}
